import React, { useState, useEffect } from "react";
import { FiSearch } from "react-icons/fi";
import toast, { Toaster } from "react-hot-toast";
import { useLazyGetSearchBatchListQuery } from "../../../State/Api/AddPurchaseApiSlice";

// Helper for expiry label & color
const formatExpiry = (expiry) => {
  if (!expiry) return "";
  if (expiry.includes("/")) return expiry;
  if (expiry.includes("-")) {
    const [year, month] = expiry.split("-");
    return `${month}/${year.slice(-2)}`;
  }
  return expiry;
};
const getExpiryClass = (expiryStr) => {
  if (!expiryStr) return "";
  let month, year;
  if (expiryStr.includes("/")) {
    [month, year] = expiryStr.split('/');
    year = year.length === 2 ? "20" + year : year;
  } else if (expiryStr.includes("-")) {
    [year, month] = expiryStr.split('-');
  }
  const expiryDate = new Date(year, month - 1, 1);
  const today = new Date();
  const nearExpiry = new Date();
  nearExpiry.setMonth(today.getMonth() + 3);
  if (expiryDate < today) return "expired";
  if (expiryDate <= nearExpiry) return "near-expiry";
  return "normal";
};

const ReturnBatchModal = ({ open, onClose, onSelect, productId, productName }) => {
  const [searchValue, setSearchValue] = useState("");
  const [batches, setBatches] = useState([]);
  const [triggerGetBatches, { isFetching }] = useLazyGetSearchBatchListQuery();

  useEffect(() => {
    if (!open) return;
    const pharmaId = localStorage.getItem("userUniqueID");
    if (!pharmaId || !productId) return;

    const fetchBatches = async () => {
      try {
        const result = await triggerGetBatches({
          pharmaId,
          ProductID: productId,
          batchnum: searchValue.trim() ? searchValue : undefined,
        }).unwrap();
        setBatches(Array.isArray(result) ? result : []);
      } catch (err) {
        setBatches([]);
      }
    };

    const debounce = setTimeout(fetchBatches, 400);
    return () => clearTimeout(debounce);
  }, [searchValue, triggerGetBatches, open, productId]);

  const handleCreateNewBatch = () => {
    onSelect({ batchNo: searchValue, isNewBatch: true });
    onClose();
    toast.success(`New batch "${searchValue}" created!`);
  };

  if (!open) return null;

  // Inline style for expiry label
  const expiryChipStyle = {
    fontSize: "10px", borderRadius: "4px", padding: "2px 6px", marginLeft: "8px", fontWeight: 600, display: "inline-block"
  };

  return (
    <>
      <style>{`
        .batch-modal-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1100;
          display: flex; align-items: center; justify-content: center;
        }
        .batch-modal-content {
          background: #fff; border-radius: 12px; width: 1000px; max-width: 95vw; box-shadow: 0 8px 32px rgba(0,0,0,0.12);
          min-height: 500px; max-height: 85vh; overflow: hidden; display: flex; flex-direction: column;
        }
        .batch-modal-search-row { padding: 18px 24px 16px 24px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #e5e7eb; }
        .batch-modal-search-box {
          flex: 1; font-size: 14px; padding: 10px 16px 10px 40px; border: 2px solid #d1d5db;
          border-radius: 8px; outline: none;
          background-image: url("data:image/svg+xml,%3Csvg ... %3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: 12px center; background-size: 18px;
        }
        .batch-modal-search-box:focus { border-color: #10b981; }
        .batch-modal-table-container { overflow-y: auto; }
        .batch-modal-table { width: 100%; border-collapse: collapse; }
        .batch-modal-table th, .batch-modal-table td { text-align: left; padding: 14px 16px; border-bottom: 1px solid #f3f4f6; }
        .batch-modal-table th {
          color: #6b7280; font-size: 11px; background: #fafafa; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.3px; position: sticky; top: 0; z-index: 10;
        }
        .batch-modal-table tbody tr { cursor: pointer; transition: background 0.15s; }
        .batch-modal-table tbody tr:hover { background: #f9fafb; }
        .create-new-batch-row { cursor: pointer; padding: 16px 24px; background: #f0fdf4; border-bottom: 1px solid #dcfce7; transition: background 0.2s; display: flex; align-items: center; gap: 10px; }
        .create-new-batch-row:hover { background: #dcfce7; }
      `}</style>

      <div className="batch-modal-backdrop" onClick={onClose}>
        <div className="batch-modal-content" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"20px 24px 16px 24px", borderBottom:"1px solid #e5e7eb"}}>
            <div>
              <h3 style={{fontSize:"18px", margin:0, color:"#111827", fontWeight:"600"}}>Search Batch for '{productName || "Product"}'</h3>
              <p style={{fontSize:"13px", color:"#6b7280", marginTop:"4px", marginBottom:0}}>Start typing to search available batches</p>
            </div>
            <button onClick={onClose} style={{fontSize:"20px", border:"none", background:"none", color:"#9ca3af", cursor:"pointer", padding:"4px", lineHeight:1}}>✕</button>
          </div>

          {/* Search bar */}
          <div style={{padding:"16px 24px", borderBottom:"1px solid #eee", position:"relative"}}>
            <input
              type="text"
              placeholder="Search batch number..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{width:"100%", padding:"8px 36px 8px 10px", borderRadius:"6px", border:"1px solid #ccc", fontSize:"14px"}}
              autoFocus
            />
            <FiSearch style={{position:"absolute", right:"34px", top:"50%", transform:"translateY(-50%)", color:"#555", fontSize:"18px"}} />
          </div>

          {/* Table */}
          <div className="batch-modal-table-container" style={{maxHeight:"340px"}}>
            {isFetching ? (
              <p style={{textAlign:"center", padding:"20px", fontSize:"14px", color:"#555"}}>Loading batches...</p>
            ) : batches.length > 0 ? (
              <table className="batch-modal-table">
                <thead>
                  <tr>
                    <th>BATCH NO</th>
                    <th>PACK</th>
                    <th>EXPIRY</th>
                    <th>MRP</th>
                    <th>STOCK</th>
                    <th>RETURN</th>
                    <th>DISTRIBUTOR</th>
                    <th>INVOICE NO</th>
                    <th>INVOICE DATE</th>
                    <th>PUR RATE</th>
                    <th>NET PUR RATE</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((batch, i) => {
                    const expiryStatus = getExpiryClass(formatExpiry(batch.expiry));
                    return (
                      <tr key={i} onClick={() => {
                          onSelect({
                            batchNo: batch.batchNo,
                            pack: batch.pack,
                            expiry: formatExpiry(batch.expiry),
                            mrp: batch.mrp,
                            stock: batch.stock,
                            return: batch.returnQty || batch.return,
                            distributor: batch.distributor || batch.distName,
                            invoiceNo: batch.invoiceNo,
                            invoiceDate: batch.invoiceDate,
                            purchaseRate: batch.purcRate || batch.purchaseRate,
                            netPurchaseRate: batch.netPurchaseRate || batch.purchaseRate,
                          });
                          onClose();
                          toast.success("Batch selected!");
                        }}>
                        <td style={{fontWeight:600}}>{batch.batchNo}</td>
                        <td>{batch.pack}</td>
                        <td>
                          {formatExpiry(batch.expiry)}
                          {expiryStatus === "expired" && <span style={{...expiryChipStyle, background:"#ea580c", color:"#fff"}}>Expired</span>}
                          {expiryStatus === "near-expiry" && <span style={{...expiryChipStyle, background:"#ef4444", color:"#fff"}}>Near Expiry</span>}
                        </td>
                        <td>₹{batch.mrp}</td>
                        <td>{batch.stock}</td>
                        <td>{batch.returnQty || batch.return}</td>
                        <td>{batch.distributor || batch.distName}</td>
                        <td>{batch.invoiceNo}</td>
                        <td>{batch.invoiceDate}</td>
                        <td>₹{batch.purcRate || batch.purchaseRate}</td>
                        <td>₹{batch.netPurchaseRate || batch.purchaseRate}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : searchValue.trim() ? (
              <div className="create-new-batch-row" onClick={handleCreateNewBatch}>Create new batch '{searchValue}'</div>
            ) : (
              <p style={{textAlign:"center", padding:"20px", color:"#777", fontSize:"13px"}}>
                {open ? "No batch ID found." : "Start typing to search for batches"}
              </p>
            )}
          </div>
        </div>
      </div>
      <Toaster />
    </>
  );
};

export default ReturnBatchModal;
